import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import invoiceService from '../services/invoiceService';
import './WorkerInvoicesWithReversals.css';

function WorkerInvoicesWithReversals() {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [expandedInvoice, setExpandedInvoice] = useState(null);
  const [selectedTransfers, setSelectedTransfers] = useState({});
  const [processingCreditNote, setProcessingCreditNote] = useState(null);
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [currentInvoiceId, setCurrentInvoiceId] = useState(null);
  const [reversalReason, setReversalReason] = useState('TRANSFER_REVERSAL');
  const [reversalNotes, setReversalNotes] = useState('');

  useEffect(() => {
    fetchInvoicesWithReversals();
  }, []);

  const getReasonLabel = (reasonType) => {
    const labels = {
      'CHARGEBACK': 'Chargeback',
      'REFUND_REQUESTED': 'Rimborso',
      'DATA_ERROR': 'Errore Dati',
      'DUPLICATE': 'Duplicato',
      'WORKER_DISPUTE': 'Contestazione',
      'ADMIN_CORRECTION': 'Correzione',
      'TRANSFER_REVERSAL': 'Storno',
      'OTHER': 'Altro'
    };
    return labels[reasonType] || reasonType;
  };

  const fetchInvoicesWithReversals = async () => {
    try {
      setLoading(true);
      const data = await invoiceService.getInvoicesWithReversals();

      if (!data.success) {
        throw new Error(data.message || 'Errore nel caricamento');
      }

      setInvoices(data.data.invoices);
    } catch (err) {
      setError('Errore nel caricamento delle fatture: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateCreditNote = async (invoiceId) => {
    const transferIds = selectedTransfers[invoiceId] || [];

    if (transferIds.length === 0) {
      setError('Seleziona almeno un transfer per generare la nota di credito');
      return;
    }

    // Show reason modal
    setCurrentInvoiceId(invoiceId);
    setShowReasonModal(true);
  };

  const handleConfirmCreditNote = async () => {
    const invoiceId = currentInvoiceId;
    const transferIds = selectedTransfers[invoiceId] || [];

    try {
      setProcessingCreditNote(invoiceId);
      setError(null);
      setShowReasonModal(false);

      const data = await invoiceService.generateCreditNote(invoiceId, transferIds, reversalReason, reversalNotes);

      if (!data.success) {
        throw new Error(data.message);
      }

      setSuccess(`✅ Nota credito ${data.data.creditNote.creditNoteNumber} creata in bozza! Motivo: ${reversalReason}`);
      setSelectedTransfers(prev => ({ ...prev, [invoiceId]: [] }));

      // Reset modal state
      setReversalReason('TRANSFER_REVERSAL');
      setReversalNotes('');
      setCurrentInvoiceId(null);

      // Refresh data
      await fetchInvoicesWithReversals();

    } catch (err) {
      setError('Errore: ' + err.message);
    } finally {
      setProcessingCreditNote(null);
    }
  };

  const handleSendToAcube = async (creditNoteId, creditNoteNumber) => {
    if (!window.confirm(`Confermi invio nota credito ${creditNoteNumber} a Acube?`)) {
      return;
    }

    try {
      setProcessingCreditNote(creditNoteId);
      setError(null);

      const data = await invoiceService.sendCreditNoteToAcube(creditNoteId);

      if (!data.success) {
        throw new Error(data.message);
      }

      setSuccess(`✅ Nota credito ${creditNoteNumber} inviata ad Acube!`);

      // Refresh data
      await fetchInvoicesWithReversals();

    } catch (err) {
      setError('Errore invio Acube: ' + err.message);
    } finally {
      setProcessingCreditNote(null);
    }
  };

  const handleDownloadPdf = async (creditNoteId, creditNoteNumber) => {
    try {
      const blob = await invoiceService.downloadCreditNotePdf(creditNoteId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `credit-note-${creditNoteNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

    } catch (err) {
      setError('Errore download PDF: ' + err.message);
    }
  };

  const handleDeleteCreditNote = async (creditNoteId, creditNoteNumber) => {
    if (!window.confirm(`Confermi eliminazione nota credito ${creditNoteNumber}?`)) {
      return;
    }

    try {
      setProcessingCreditNote(creditNoteId);
      setError(null);

      const data = await invoiceService.deleteCreditNote(creditNoteId);

      if (!data.success) {
        throw new Error(data.message);
      }

      setSuccess(`🗑️ Nota credito ${creditNoteNumber} eliminata!`);

      // Refresh data
      await fetchInvoicesWithReversals();

    } catch (err) {
      setError('Errore eliminazione: ' + err.message);
    } finally {
      setProcessingCreditNote(null);
    }
  };

  const toggleTransferSelection = (invoiceId, transferId) => {
    setSelectedTransfers(prev => {
      const current = prev[invoiceId] || [];
      const isSelected = current.includes(transferId);

      return {
        ...prev,
        [invoiceId]: isSelected
          ? current.filter(id => id !== transferId)
          : [...current, transferId]
      };
    });
  };

  const formatCurrency = (cents) => {
    return `€${(cents / 100).toFixed(2)}`;
  };

  if (loading) {
    return (
      <div className="invoices-with-reversals-page">
        <h1>🔴 Fatture con Transfer Reversati</h1>
        <div className="loading">Caricamento...</div>
      </div>
    );
  }

  return (
    <div className="invoices-with-reversals-page">
      <div className="page-header">
        <h1>🔴 Fatture con Transfer Reversati</h1>
        <p className="subtitle">
          Gestisci note di credito per transfer stornati (chargeback, correzioni, fraud)
        </p>
      </div>

      {error && (
        <div className="alert alert-error">
          {error}
          <button onClick={() => setError(null)}>✕</button>
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          {success}
          <button onClick={() => setSuccess(null)}>✕</button>
        </div>
      )}

      {invoices.length === 0 ? (
        <div className="no-data">
          <div className="icon">✅</div>
          <h3>Nessuna fattura con reversal</h3>
          <p>Tutte le fatture sono pulite - ottimo lavoro!</p>
        </div>
      ) : (
        <div className="invoices-list">
          {invoices.map(invoice => (
            <div key={invoice.id} className="invoice-card">

              <div className="invoice-header">
                <div className="invoice-info">
                  <h3>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(`/admin/invoices/${invoice.id}`);
                      }}
                      className="invoice-link"
                      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'inherit', textDecoration: 'underline' }}
                      title="Visualizza dettagli fattura"
                    >
                      {invoice.invoiceNumber}
                    </button>
                  </h3>
                  <p className="worker-name">
                    {invoice.workerPaymentProfile.verified_first_name} {invoice.workerPaymentProfile.verified_last_name}
                  </p>
                  <p className="date">{new Date(invoice.generatedAt).toLocaleDateString('it-IT')}</p>
                </div>

                <div className="reversal-stats">
                  <div className={`stat ${invoice.reversalStats.requiresAction ? 'urgent' : 'handled'}`}>
                    <span className="label">Da Gestire:</span>
                    <strong>{invoice.reversalStats.notHandledCount}</strong>
                  </div>
                  <div className="stat">
                    <span className="label">Già Gestiti:</span>
                    <strong>{invoice.reversalStats.alreadyHandledCount}</strong>
                  </div>
                  <div className="stat amount">
                    <span className="label">Da Rimborsare:</span>
                    <strong className="refund-amount">{formatCurrency(invoice.reversalStats.totalToRefund)}</strong>
                  </div>
                </div>

                <button
                  className="toggle-btn"
                  onClick={() => setExpandedInvoice(
                    expandedInvoice === invoice.id ? null : invoice.id
                  )}
                >
                  {expandedInvoice === invoice.id ? '▲ Chiudi' : '▼ Dettagli'}
                </button>
              </div>

              {expandedInvoice === invoice.id && (
                <div className="invoice-details">
                  <h4>Transfer Reversati ({invoice.reversedTransfers.length})</h4>

                  <table className="transfers-table">
                    <thead>
                      <tr>
                        <th>Selezione</th>
                        <th>ID Transfer</th>
                        <th>Venue</th>
                        <th>Importo Orig.</th>
                        <th>Reversato</th>
                        <th>Tipo Storno</th>
                        <th>Netto Trattenuto</th>
                        <th>Importo da Stornare</th>
                        <th>Stato</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoice.reversedTransfers.map(transfer => {
                        const netRetained = transfer.amount - transfer.amountReversed;
                        const isFullReversal = transfer.reversed === true;
                        const reversalType = isFullReversal ? 'Totale' : 'Parziale';
                        const reversalClass = isFullReversal ? 'reversal-full' : 'reversal-partial';

                        return (
                          <tr key={transfer.id} className={transfer.isHandled ? 'handled' : ''}>
                            <td>
                              {!transfer.isHandled && (
                                <input
                                  type="checkbox"
                                  checked={(selectedTransfers[invoice.id] || []).includes(transfer.id)}
                                  onChange={() => toggleTransferSelection(invoice.id, transfer.id)}
                                />
                              )}
                            </td>
                            <td className="transfer-id">{transfer.id.substring(0, 20)}...</td>
                            <td>{transfer.tipPaymentIntent?.venue?.name || 'N/A'}</td>
                            <td>{formatCurrency(transfer.amount)}</td>
                            <td className="reversed-amount">
                              -{formatCurrency(transfer.amountReversed)}
                            </td>
                            <td>
                              <span className={`reversal-badge ${reversalClass}`}>
                                {isFullReversal ? '🔴' : '🟡'} {reversalType}
                              </span>
                            </td>
                            <td className="net-retained">
                              {formatCurrency(netRetained)}
                            </td>
                            <td className="fee-refund-total">
                              <strong>{formatCurrency(transfer.feeTotalToRefund || 0)}</strong>
                            </td>
                            <td>
                              {transfer.isHandled ? (
                                <span className="badge handled">✅ Gestito</span>
                              ) : (
                                <span className="badge pending">⏳ Da Gestire</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  {invoice.reversalStats.notHandledCount > 0 && (
                    <div className="actions">
                      <button
                        className="credit-note-btn"
                        onClick={() => handleGenerateCreditNote(invoice.id)}
                        disabled={
                          (selectedTransfers[invoice.id] || []).length === 0 ||
                          processingCreditNote === invoice.id
                        }
                      >
                        {processingCreditNote === invoice.id ? (
                          '⏳ Elaborazione...'
                        ) : (
                          <>
                            📝 Crea Nota Credito (Bozza)
                            {(selectedTransfers[invoice.id] || []).length > 0 && (
                              <span className="count">
                                ({(selectedTransfers[invoice.id] || []).length} selezionati)
                              </span>
                            )}
                          </>
                        )}
                      </button>
                    </div>
                  )}

                  {invoice.creditNotes && invoice.creditNotes.length > 0 && (
                    <div className="credit-notes-section">
                      <h4>Note di Credito Emesse ({invoice.creditNotes.length})</h4>
                      <div className="credit-notes-list">
                        {invoice.creditNotes.map(cn => (
                          <div key={cn.id} className="credit-note-item">
                            <div className="cn-info">
                              <strong>{cn.creditNoteNumber}</strong>
                              <span className="cn-amount">{formatCurrency(cn.totalRefund)}</span>
                              <span className={`cn-status status-${cn.status.toLowerCase()}`}>
                                {cn.status}
                              </span>
                              {cn.reasonType && (
                                <span className={`cn-reason reason-${cn.reasonType.toLowerCase()}`}>
                                  {getReasonLabel(cn.reasonType)}
                                </span>
                              )}
                              <span className="cn-date">{new Date(cn.generatedAt).toLocaleDateString('it-IT')}</span>
                              {cn.reasonNotes && (
                                <span className="cn-notes" title={cn.reasonNotes}>
                                  💬 {cn.reasonNotes}
                                </span>
                              )}
                            </div>
                            <div className="cn-actions">
                              {cn.status === 'DRAFT' && (
                                <>
                                  <button
                                    className="btn-send-acube"
                                    onClick={() => handleSendToAcube(cn.id, cn.creditNoteNumber)}
                                    disabled={processingCreditNote === cn.id}
                                  >
                                    📤 Invia ad Acube
                                  </button>
                                  <button
                                    className="btn-delete"
                                    onClick={() => handleDeleteCreditNote(cn.id, cn.creditNoteNumber)}
                                    disabled={processingCreditNote === cn.id}
                                  >
                                    🗑️ Elimina
                                  </button>
                                </>
                              )}
                              {cn.status === 'SENT_TO_ACUBE' && (
                                <button
                                  className="btn-download-pdf"
                                  onClick={() => handleDownloadPdf(cn.id, cn.creditNoteNumber)}
                                >
                                  📄 Scarica PDF
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Reason Modal */}
      {showReasonModal && (
        <div className="invoice-reversal-modal-overlay" onClick={() => setShowReasonModal(false)}>
          <div className="invoice-reversal-modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Motivo Nota di Credito</h3>
            <p>Seleziona il motivo per cui stai creando questa nota di credito:</p>

            <div className="form-group">
              <label>Motivo:</label>
              <select
                value={reversalReason}
                onChange={(e) => setReversalReason(e.target.value)}
                className="reason-select"
              >
                <option value="TRANSFER_REVERSAL">Storno Transfer</option>
                <option value="CHARGEBACK">Chargeback Stripe</option>
                <option value="REFUND_REQUESTED">Rimborso Richiesto</option>
                <option value="DATA_ERROR">Errore Dati Fattura</option>
                <option value="DUPLICATE">Fattura Duplicata</option>
                <option value="WORKER_DISPUTE">Contestazione Worker</option>
                <option value="ADMIN_CORRECTION">Correzione Amministrativa</option>
                <option value="OTHER">Altro</option>
              </select>
            </div>

            <div className="form-group">
              <label>Note {reversalReason === 'OTHER' && <span className="required">*</span>}:</label>
              <textarea
                value={reversalNotes}
                onChange={(e) => setReversalNotes(e.target.value)}
                placeholder="Descrivi il motivo della nota di credito..."
                rows="4"
                className="reason-notes"
              />
            </div>

            <div className="modal-actions">
              <button
                className="btn-cancel"
                onClick={() => {
                  setShowReasonModal(false);
                  setReversalReason('TRANSFER_REVERSAL');
                  setReversalNotes('');
                }}
              >
                Annulla
              </button>
              <button
                className="btn-confirm"
                onClick={handleConfirmCreditNote}
                disabled={reversalReason === 'OTHER' && !reversalNotes.trim()}
              >
                Conferma Creazione
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default WorkerInvoicesWithReversals;
